import { useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react'
import { Session } from '@supabase/supabase-js'
import { ActivityTracker } from './lib/activityTracker'
import { InactivityWarningModal } from './components/shared/InactivityWarningModal'
import { SubscriptionProvider } from './contexts/SubscriptionContext'
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { supabase } from './lib/supabase'

// Lazy-loaded route components (named exports → default export adapter)
const LandingPage = lazy(() => import('./components/LandingPage').then(m => ({ default: m.LandingPage })))
const TeacherDashboard = lazy(() => import('./components/TeacherDashboard').then(m => ({ default: m.TeacherDashboard })))
const StudentDashboard = lazy(() => import('./components/StudentDashboard').then(m => ({ default: m.StudentDashboard })))
const TeacherClassPage = lazy(() => import('./components/TeacherClassPage').then(m => ({ default: m.TeacherClassPage })))
const StudentClassPage = lazy(() => import('./components/StudentClassPage').then(m => ({ default: m.StudentClassPage })))
const AssignmentStatusPage = lazy(() => import('./components/AssignmentStatusPage').then(m => ({ default: m.AssignmentStatusPage })))
const SubmissionFeedbackPage = lazy(() => import('./components/SubmissionFeedbackPage').then(m => ({ default: m.SubmissionFeedbackPage })))
const ViewReturnedAssignment = lazy(() => import('./components/ViewReturnedAssignment').then(m => ({ default: m.ViewReturnedAssignment })))
const ResetPassword = lazy(() => import('./components/ResetPassword').then(m => ({ default: m.ResetPassword })))
const EmailConfirmation = lazy(() => import('./components/EmailConfirmation').then(m => ({ default: m.EmailConfirmation })))
const AuthCallback = lazy(() => import('./components/AuthCallback').then(m => ({ default: m.AuthCallback })))
const AccountDeleted = lazy(() => import('./components/AccountDeleted').then(m => ({ default: m.AccountDeleted })))
const SignupWithInvitation = lazy(() => import('./components/SignupWithInvitation').then(m => ({ default: m.SignupWithInvitation })))
const AssignmentFormWrapper = lazy(() => import('./components/assignments/teacher/AssignmentFormWrapper').then(m => ({ default: m.AssignmentFormWrapper })))
const StudentSubmissionPage = lazy(() => import('./components/assignments/student/StudentSubmissionPage').then(m => ({ default: m.StudentSubmissionPage })))
const ResubmitAssignmentPage = lazy(() => import('./pages/ResubmitAssignmentPage').then(m => ({ default: m.ResubmitAssignmentPage })))
const SettingsPage = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsPage })))
const ProfileTab = lazy(() => import('./pages/Settings/ProfileTab').then(m => ({ default: m.ProfileTab })))
const AccountTab = lazy(() => import('./pages/Settings/AccountTab').then(m => ({ default: m.AccountTab })))
const FeedbackTab = lazy(() => import('./pages/Settings/FeedbackTab').then(m => ({ default: m.FeedbackTab })))
const BillingTab = lazy(() => import('./pages/Settings/BillingTab').then(m => ({ default: m.BillingTab })))

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50">
    <div className="bg-white rounded-2xl shadow-xl p-8 flex items-center space-x-4">
      <svg className="animate-spin h-6 w-6 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span className="text-lg font-medium text-gray-700">Loading...</span>
    </div>
  </div>
)

type UserRole = 'teacher' | 'student' | null

interface Class {
  id: string
  name: string
  teacher_id: string
  class_code: string
  created_at: string
}

function AppContent() {
  const [searchParams] = useSearchParams()
  const [session, setSession] = useState<Session | null>(null)
  const [userRole, setUserRole] = useState<UserRole>(null)
  const [loading, setLoading] = useState(true)
  const [classes, setClasses] = useState<Class[]>([])
  const [loadingTeacherClasses, setLoadingTeacherClasses] = useState(false)
  const teacherClassesRequestRef = useRef(0)
  const authRequestRef = useRef(0)
  const [showInactivityWarning, setShowInactivityWarning] = useState(false)
  const [activityTracker, setActivityTracker] = useState<ActivityTracker | null>(null)

  const handleTimeout = useCallback(() => {
    // Session has expired, redirect to landing page
    window.location.href = '/'
  }, [])

  const handleWarning = useCallback(() => {
    setShowInactivityWarning(true)
  }, [])

  const handleExtendSession = useCallback(() => {
    activityTracker?.extendSession()
    setShowInactivityWarning(false)
  }, [activityTracker])

  const handleCloseWarning = useCallback(() => {
    setShowInactivityWarning(false)
    // User chose not to extend, let the timeout proceed
  }, [])

  // Initialize activity tracker when session changes
  useEffect(() => {
    if (session) {
      const tracker = new ActivityTracker(handleTimeout, handleWarning)
      setActivityTracker(tracker)
      tracker.start()
      return () => tracker.stop()
    } else {
      setActivityTracker(null)
    }
  }, [session, handleTimeout, handleWarning])

  const fetchUserRole = useCallback(async (userId: string, currentSession: Session, requestId: number) => {
    try {
      // First check metadata
      const metadataRole = currentSession.user?.user_metadata?.role as UserRole
      if (metadataRole) {
        if (requestId !== authRequestRef.current) return
        setUserRole(metadataRole)
        if (metadataRole !== 'teacher') {
          teacherClassesRequestRef.current += 1
          setLoadingTeacherClasses(false)
        }
        setLoading(false)
        return
      }

      // Fallback to database
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle()

      if (error) throw error
      if (requestId !== authRequestRef.current) return

      if (data?.role) {
        // Only attempt to update metadata if the session is not close to expiring
        const sessionExpiresAt = currentSession.expires_at ? currentSession.expires_at * 1000 : 0 // Convert to milliseconds
        const timeUntilExpiry = sessionExpiresAt - Date.now()
        const minutesUntilExpiry = timeUntilExpiry / 1000 / 60

        // Update metadata only if we have more than 30 minutes until session expiry
        if (minutesUntilExpiry > 30) {
          try {
            await supabase.auth.updateUser({
              data: { role: data.role }
            })
          } catch {
            // Silently ignore metadata update errors
            // The role is already in our app state, so this is non-critical
          }
        }

        setUserRole(data.role as UserRole)
        if (data.role !== 'teacher') {
          teacherClassesRequestRef.current += 1
          setLoadingTeacherClasses(false)
        }
      } else {
        setUserRole(null)
        teacherClassesRequestRef.current += 1
        setLoadingTeacherClasses(false)
      }
    } catch (error) {
      if (requestId !== authRequestRef.current) return
      // Only log critical errors (not metadata-related)
      if (error instanceof Error && !error.message.includes('403')) {
        console.error('Error fetching user role:', error)
      }
      setUserRole(null)
      teacherClassesRequestRef.current += 1
      setLoadingTeacherClasses(false)
    } finally {
      if (requestId === authRequestRef.current) {
        setLoading(false)
      }
    }
  }, [])

  // Fetch appropriate classes when userRole changes
  useEffect(() => {
    if (session && userRole === 'teacher') {
      const requestId = ++teacherClassesRequestRef.current
      setLoadingTeacherClasses(true)
      void fetchTeacherClasses(session.user.id, requestId)
    }
  }, [userRole, session])

  useEffect(() => {
    // Listen for auth changes, including the initial session
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Handle specific auth events
      switch (_event) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN':
          if (session) {
            const requestId = ++authRequestRef.current
            teacherClassesRequestRef.current += 1
            setLoadingTeacherClasses(true)
            setClasses([])
            setUserRole(null)
            setLoading(true)
            setSession(session)
            fetchUserRole(session.user.id, session, requestId)
          } else if (_event === 'INITIAL_SESSION') {
            setLoading(false)
          }
          break
        case 'SIGNED_OUT':
          authRequestRef.current += 1
          teacherClassesRequestRef.current += 1
          setSession(null)
          setUserRole(null)
          setClasses([])
          setLoadingTeacherClasses(false)
          setLoading(false)
          break
        case 'USER_UPDATED':
          // Only update session on user updates
          setSession(session)
          break
        default:
          // Ignore other events
          break
      }
    })

    return () => subscription.unsubscribe()
  }, [fetchUserRole])


  const fetchTeacherClasses = async (userId: string, requestId: number) => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('teacher_id', userId)

      if (error) throw error
      if (requestId !== teacherClassesRequestRef.current) return
      setClasses(data || [])
    } catch (error) {
      if (requestId === teacherClassesRequestRef.current) {
        console.error('Error fetching teacher classes:', error)
      }
    } finally {
      if (requestId === teacherClassesRequestRef.current) {
        setLoadingTeacherClasses(false)
      }
    }
  }

  // Show loading screen while checking session or loading user data
  if (userRole === 'teacher' && loadingTeacherClasses) {
    return <LoadingFallback />
  }
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="bg-white rounded-2xl shadow-xl p-8 flex items-center space-x-4">
          <svg className="animate-spin h-6 w-6 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-lg font-medium text-gray-700">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          session ? (
            <Navigate 
              to={`/dashboard${searchParams.toString() ? `?${searchParams.toString()}` : ''}`} 
              replace 
            />
          ) : (
            <LandingPage />
          )
        } 
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/confirm-email" element={<EmailConfirmation />} />
      {/* Own-domain auth redirect handlers — prevent raw *.supabase.co links in auth emails */}
      <Route path="/auth/confirm" element={<AuthCallback />} />
      <Route path="/auth/reset" element={<AuthCallback />} />
      <Route path="/auth/magiclink" element={<AuthCallback />} />
      <Route path="/auth/change-email" element={<AuthCallback />} />
      <Route path="/signup" element={<SignupWithInvitation />} />
      {/* Account deleted page - explicitly prevent redirect to dashboard */}
      <Route 
        path="/account-deleted" 
        element={
          session ? (
            <AccountDeleted />
          ) : (
            <AccountDeleted />
          )
        } 
      />

      {/* Protected Routes */}
      {session && userRole ? (
        <>
          {/* Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              userRole === 'teacher' ? (
                <TeacherDashboard session={session} classes={classes} onClassesChange={setClasses} userRole={userRole} />
              ) : (
                <StudentDashboard session={session} userRole={userRole} />
              )
            }
          />

          {/* Settings Routes */}
          <Route path="/settings" element={<SettingsPage session={session} classes={classes} userRole={userRole} />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ProfileTab />} />
            <Route path="account" element={<AccountTab />} />
            <Route path="feedback" element={<FeedbackTab />} />
            <Route path="billing" element={<BillingTab />} />
          </Route>

          {/* Teacher Routes */}
          {/* Assignment Form Routes */}
          <Route
            path="/dashboard/teacher/class/:classId/assignments/new"
            element={
              userRole === 'teacher' ? (
                <AssignmentFormWrapper session={session} mode="create" />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/dashboard/teacher/class/:classId/assignments/reuse/:sourceId"
            element={
              userRole === 'teacher' ? (
                <AssignmentFormWrapper session={session} mode="reuse" />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/dashboard/teacher/class/:classId/assignments/edit/:assignmentId"
            element={
              userRole === 'teacher' ? (
                <AssignmentFormWrapper session={session} mode="edit" />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/dashboard/teacher/class/:classId"
            element={
              userRole === 'teacher' ? (
                <TeacherClassPage session={session} classes={classes} userRole={userRole} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/dashboard/classes/:classId/assignments/:assignmentId"
            element={
              userRole === 'teacher' ? (
                <AssignmentStatusPage session={session} classes={classes} userRole={userRole} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/dashboard/classes/:classId/assignments/:assignmentId/submissions/:submissionId"
            element={
              userRole === 'teacher' ? (
                <SubmissionFeedbackPage session={session} classes={classes} userRole={userRole} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
           path="/assignment/:submissionId/resubmit"
           element={
             userRole === 'student' ? (
               <ResubmitAssignmentPage session={session} userRole={userRole} />
             ) : (
               <Navigate to="/dashboard" replace />
             )
           }
         />

          {/* Student Routes */}
          <Route
            path="/dashboard/student/class/:classId"
            element={

              userRole === 'student' ? (

                <StudentClassPage session={session} userRole={userRole} />

              ) : (

                <Navigate to="/dashboard" replace />

              )

            }

          />

          <Route

            path="/dashboard/student/class/:classId/assignment/:assignmentId/submit"

            element={

              userRole === 'student' ? (

                <StudentSubmissionPage session={session} />

              ) : (

                <Navigate to="/dashboard" replace />

              )

            }

          />
          <Route
            path="/dashboard/classes/:classId/assignments/:assignmentId/returned/:submissionId"
            element={
              userRole === 'student' ? (
                <ViewReturnedAssignment session={session} userRole={userRole} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
        </>
      ) : null}

      {/* Catch all redirect */}
      <Route 
        path="*" 
        element={
          <Navigate 
            to={session ? '/dashboard' : '/'} 
            replace 
          />
        } 
      />
        </Routes>
      </Suspense>

    {/* Inactivity Warning Modal */}
    {showInactivityWarning && (
      <InactivityWarningModal
        onExtendSession={handleExtendSession}
        onClose={handleCloseWarning}
      />
    )}
    </>
  )
}

// Wrap the app content in BrowserRouter
function App() {
  return (
    <BrowserRouter>
      <SubscriptionProvider>
        <AppContent />
      </SubscriptionProvider>
    </BrowserRouter>
  )
}

export default App
